import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { toast } from "react-toastify";

import useUnreadToken from "common/datahooks/useUnreadToken";

const UnreadMessagesWebsocketContext = createContext<WebSocket>(null);

const wsBaseUrl = import.meta.env.VITE_INBOX_WS_URL;

export function UnreadMessagesWebsocketProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { unreadToken } = useUnreadToken();
  const [websocket, setWebsocket] = useState<WebSocket>();

  useEffect(() => {
    if (unreadToken) {
      const websocketUrl = `${wsBaseUrl}/unread?authorization=${unreadToken}`;

      const websocketInstance = new WebSocket(websocketUrl);
      websocketInstance.onerror = () => {
        toast.error("Loading unread messages failed, please refresh the page");
      };
      setWebsocket(websocketInstance);

      return () => {
        if (websocketInstance.readyState === WebSocket.OPEN) {
          websocketInstance.close();
        }
      };
    }
  }, [unreadToken]);

  return (
    <UnreadMessagesWebsocketContext.Provider value={websocket}>
      {children}
    </UnreadMessagesWebsocketContext.Provider>
  );
}

export function useUnreadMessagesWebsocketContext(
  onDataReceived: (data: {
    unreadCount: number;
    shouldRefetch: boolean;
  }) => void,
) {
  const unreadMessagesWebsocket = useContext(UnreadMessagesWebsocketContext);

  useEffect(() => {
    if (unreadMessagesWebsocket) {
      function onMessage(event: MessageEvent) {
        const { unread_count: unreadCount, refetch: shouldRefetch } =
          JSON.parse(event.data);
        onDataReceived({ unreadCount, shouldRefetch });
      }

      unreadMessagesWebsocket.addEventListener("message", onMessage);
      return () => {
        unreadMessagesWebsocket.removeEventListener("message", onMessage);
      };
    }
  }, [unreadMessagesWebsocket, onDataReceived]);

  return { unreadMessagesWebsocket };
}
